import React, {useEffect, useImperativeHandle, useRef, useState, forwardRef, useCallback} from 'react'
import styled from 'styled-components'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import breakpoints from '../../../config/Breakpoints'
import ColorConfig from '../../../config/ColorConfig'
import BooksitoutImages from '../../../images/BooksitoutImages'
import SearchHistoryResponse from '../../../common/response/SearchHistoryResponse'
import AutoCompleteResponse from '../../../common/response/AutoCompleteResponse'

interface Props {
	placeholder: string

	query: string
	queryHistories: SearchHistoryResponse[]
	querySuggestions: AutoCompleteResponse[]

	isShowingQueryHistory: boolean
	isShowingAutoComplete: boolean
	isFocusOnAppear?: boolean
	isShouldUpdateQueryParam: boolean

	setQuery: (query: string) => void
	setIsShowingQueryHistory: (isShowingQueryHistory: boolean) => void
	setIsShowingAutoComplete: (isShowingAutoComplete: boolean) => void
	handleSubmit: (event: React.FormEvent<HTMLFormElement>, focus: () => void) => void
	addFirstItemOnSubmit: ((item: any) => void) | null

	handleQuerySuggestionClick?: (suggestion: AutoCompleteResponse) => void
}

let defaultHandleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
	e.preventDefault()
}

export interface SearchBarHandle {
	focus: () => void
}

const SearchBar = forwardRef<SearchBarHandle, Props>(({
	placeholder,

	query,
	queryHistories,
	querySuggestions,

	isShowingQueryHistory,
	isShowingAutoComplete,
	isFocusOnAppear,
	isShouldUpdateQueryParam,

	setQuery,
	setIsShowingQueryHistory,
	setIsShowingAutoComplete,
	handleSubmit = defaultHandleSubmit,
	addFirstItemOnSubmit,

	handleQuerySuggestionClick,
}, ref) => {
	const htmlInputRef = useRef<HTMLInputElement>(null)

	useImperativeHandle(ref, () => ({
		focus: () => htmlInputRef.current?.focus(),
	}))
	useEffect(() => {
		if (htmlInputRef.current && isFocusOnAppear) {
			htmlInputRef.current.focus()
		}
	}, [])

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value ?? ''

		setQuery(value)
		setHighlight(-1)
		if (!isShouldUpdateQueryParam) {
			return
		}

		if (value) {
			window.history.pushState({}, '', `?q=${value}`)
		} else {
			window.history.pushState({}, '', window.location.pathname)
		}
	}

	const handleOnFocus = () => {
		if (htmlInputRef.current) {
			htmlInputRef.current.focus()
		}

		setIsShowingAutoComplete(true)
		setIsShowingQueryHistory(true)
	}

	const handleOnBlur = () => {
		setTimeout(() => {
			setIsShowingAutoComplete(false)
			setIsShowingQueryHistory(false)
		}, 200)
		setHighlight(-1)
	}

	const [highlight, setHighlight] = useState(-1)

	const prevListTypeRef = useRef<'suggest' | 'history' | 'none'>('none')

	const showSuggest = isShowingAutoComplete && querySuggestions.length > 0
	const showHistory = !showSuggest && isShowingQueryHistory && queryHistories.length > 0

	const activeList = showSuggest ? querySuggestions : showHistory ? queryHistories : []
	const activeListLength = activeList.length

	useEffect(() => {
		if (activeListLength === 0) setHighlight(-1)
		else setHighlight(h => Math.min(h, activeListLength - 1))
	}, [activeListLength])

	useEffect(() => {
		const type: 'suggest' | 'history' | 'none' = showSuggest ? 'suggest' : showHistory ? 'history' : 'none'
		if (prevListTypeRef.current !== type) {
			setHighlight(-1) // 전환 시 리셋
			prevListTypeRef.current = type
		}
	}, [showSuggest, showHistory])

	return (
		// @ts-ignore
		<SearchForm
			onSubmit={e => {
				handleSubmit(e, handleOnFocus)
				if (addFirstItemOnSubmit && querySuggestions.length > 0) {
					addFirstItemOnSubmit(querySuggestions[0])
				}
			}}
			style={{ width: '100%' }}
		>
			<SearchButton type="submit">
				<span>
					<booksitoutIcon.search />
				</span>
			</SearchButton>

			<Input
				// @ts-ignore
				ref={htmlInputRef}
				placeholder={placeholder}
				value={query}
				autoComplete="off"
				onChange={handleInputChange}
				onFocus={handleOnFocus}
				onBlur={handleOnBlur}
				onKeyDown={e => {
					// Ignore IME composition events (for Korean, Japanese, Chinese input)
					if (e.nativeEvent.isComposing) return

					if (e.key === 'Escape') {
						if (query === '') {
							setIsShowingQueryHistory(!isShowingQueryHistory)
						} else {
							setIsShowingAutoComplete(!isShowingAutoComplete)
						}

						setHighlight(-1)
					}

					if (!activeListLength) return

					if (e.key === 'ArrowDown') {
						e.preventDefault()
						setHighlight(prev => (prev < 0 ? 0 : (prev + 1) % activeListLength))
					}
					if (e.key === 'ArrowUp') {
						e.preventDefault()
						setHighlight(prev => (prev < 0 ? activeListLength - 1 : (prev - 1 + activeListLength) % activeListLength))
					}
					if (e.key === 'Enter' && highlight >= 0) {
						e.preventDefault()
						const url = activeList[highlight]?.url
						if (url) window.location.href = url
					}
				}}
			/>

			{query && (
				<ClearButton
					type="button"
					onMouseDown={e => {
						e.preventDefault()
						setQuery('')
						if (isShouldUpdateQueryParam) {
							window.history.pushState({}, '', window.location.pathname)
						}
						htmlInputRef.current?.focus()
					}}
				>
					<booksitoutIcon.xmark />
				</ClearButton>
			)}

			{showHistory && (
				<AutocompleteBox style={{ backgroundColor: 'var(--sub-bg-color)' }}>
					{queryHistories.map((h, i) => (
						<SuggestionLink href={h.url} key={h.id ?? h.url} className={i === highlight ? 'active' : ''}>
							<SuggestionCover src={h.imageUrl} />
							<SuggestionName style={{ color: 'var(--text-color)' }}>{h.query}</SuggestionName>
							{h.secondaryQuery && <SuggestionSecondaryName>{h.secondaryQuery}</SuggestionSecondaryName>}
						</SuggestionLink>
					))}
				</AutocompleteBox>
			)}

			{showSuggest && (
				<AutocompleteBox>
					{handleQuerySuggestionClick
						? querySuggestions.map((s, i) => (
								<Suggestion
									key={s.id ?? s.url ?? `${s.name}-${i}`}
									onMouseDown={() => handleQuerySuggestionClick(s)} // onClick → onMouseDown 권장 (blur 전에 처리)
									className={i === highlight ? 'active' : ''}
								>
									<SuggestionCover src={s.imageUrl} placeholder={s.type === 'AUTHOR' ? BooksitoutImages.Placeholder.user : undefined} />
									<SuggestionName>{s.name}</SuggestionName>
									{s.type === 'AUTHOR' && <TypeBadge>저자</TypeBadge>}
									{s.secondaryName && <SuggestionSecondaryName>{s.secondaryName}</SuggestionSecondaryName>}
								</Suggestion>
						  ))
						: querySuggestions.map((s, i) => (
								<SuggestionLink
									href={s.url}
									key={s.id ?? s.url ?? `${s.name}-${i}`}
									className={i === highlight ? 'active' : ''}
									onMouseDown={e => e.stopPropagation()} // blur 방지(선택)
								>
									<SuggestionCover src={s.imageUrl} placeholder={s.type === 'AUTHOR' ? BooksitoutImages.Placeholder.user : undefined} />
									<SuggestionName>{s.name}</SuggestionName>
									{s.type === 'AUTHOR' && <TypeBadge>저자</TypeBadge>}
									{s.secondaryName && <SuggestionSecondaryName>{s.secondaryName}</SuggestionSecondaryName>}
								</SuggestionLink>
						  ))}
				</AutocompleteBox>
			)}
		</SearchForm>
	)
})

const Input = styled.input`
	flex: 1;
	min-width: 0;
	border: none;
	border-radius: 0;
	outline: none;
	padding: 0;
	font-size: 16px;
	line-height: 1.25;
	background-color: transparent !important;
	color: var(--text-color);
	caret-color: var(--primary-color);

	&::placeholder {
		color: var(--sub-text-color);
	}
`

const SearchButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	color: var(--sub-text-color);
	flex-shrink: 0;
`

const ClearButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	color: var(--sub-text-color);
	flex-shrink: 0;
	font-size: 18px;

	&:hover {
		color: var(--text-color);
	}
`

const SuggestionLink = styled.a`
	display: flex;
	align-items: center;
	text-decoration: none;
	color: black;

	padding: 10px 10px 10px 30px;

	&:hover {
        background-color: var(--hover-bg-color);
	}

	&.active {
        background-color: var(--hover-bg-color);
	}
`

const Suggestion = styled.div.attrs({
	className: 'clickable',
})`
	display: flex;
	align-items: center;
	text-decoration: none;
	color: black;

	padding: 10px 10px 10px 30px;

	&:hover {
		background-color: var(--hover-bg-color);
	}

	&.active {
		background-color: var(--hover-bg-color);
	}
`

const SearchForm = styled.form`
	display: flex;
	position: relative;
	align-items: center;
	gap: 12px;
	min-height: 52px;
	padding: 12px 20px;
	border: none;
	border-radius: 16px;
	background-color: var(--search-field-bg-color);
	transition: box-shadow 0.2s ease, background-color 0.2s ease;

	&:focus-within {
		box-shadow: none;
		background-color: var(--input-focus-bg-color);
	}
`

const AutocompleteBox = styled.div`
	position: absolute;
	width: 100%;
	top: calc(100% + 8px);
	left: 0;

	background-color: var(--card-bg-color);
	color: var(--text-color);

	border-radius: 16px;
	z-index: 1000;
	min-width: 350px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
	overflow: hidden;
`

const SuggestionImage = styled.img.attrs({
	className: 'img-fluid',
})`
	width: 40px;
	height: 40px;
	margin-right: 10px;
	border-radius: 20px;
`

const SuggestionCover: React.FC<{ src?: string | null; placeholder?: string }> = ({ src, placeholder }) => {
	const [failed, setFailed] = useState(false)
	const handleError = useCallback(() => setFailed(true), [])

	if (!src || failed) {
		return <SuggestionImage src={placeholder ?? BooksitoutImages.Placeholder.searchCover} />
	}

	return <SuggestionImage src={src} onError={handleError} />
}

const SuggestionName = styled.div`
	font-size: 16px;
	font-weight: bold;
	padding-left: 10px;
    color: var(--text-color);
`

const SuggestionSecondaryName = styled.div.attrs({
	className: 'text-secondary clamp-1-line',
})`
	font-size: 12px;
	color: var(--text-secondary-color);
	font-weight: bold;
	padding-left: 5px;
	padding-top: 10px;
`

const TypeBadge = styled.span`
	flex-shrink: 0;
	margin-left: 8px;
	padding: 2px 8px;
	border-radius: 10px;
	font-size: 11px;
	font-weight: bold;
	color: var(--text-secondary-color);
	background-color: var(--hover-bg-color);
`

export default SearchBar
